import { useState, useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import NavBar from "../Components/Navbar";
import ModalForm from "../Components/ModalForm";
import {
    Box,
    Text,
    Heading,
    Button,
    Flex,
    useDisclosure,
    HStack,
    useToast,
    Spinner,
    Checkbox,
} from "@chakra-ui/react";
import axios from "axios";
import useAPI from "../hooks/useAPI";
import CustomSpinner from "../Components/CustomSpinner";
import { useAuth } from "../Components/auth/AuthProvider";
import { useNavigate } from "react-router-dom";
import logo from "../assets/logo.jpeg";

function IndividualBusinessLoan() {
    const toast = useToast();
    const { id, loan_id } = useParams();
    const { token } = useAuth();
    const navigate = useNavigate();

    const [businessLoans, setBusinessLoans] = useState([]);
    const [businessLoading, setbusinessLoading] = useState(true);
    const [score, setScore] = useState(0);

    const { data: studentInfo, setData: setStudentInfo, loading: studentInfoLoading } = useAPI(
        `/applications/${id}`
    );


    useEffect(() => {
        const fetchLoans = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/business_loan`, {
                    params: { loan_id: loan_id },  // Automatically converts to ?student_id=123
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                setBusinessLoans(response.data[0]);
            } catch (error) {
                console.error("Error fetching business loans", error);
            } finally {
                setbusinessLoading(false);
            }
        };

        const fetchScore = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/business_loan/${id}/calculate_score`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                setScore(response.data.business_loan_score);
            } catch (error) {
                console.error("Error fetching business loans", error);
            }
        };

        fetchLoans();
        fetchScore(); // Call fetchScore here
    }, [id, score]);

    console.log("Business Loans", businessLoans);
    console.log("Business Loans Score", score);

    const { data: finances, loading: financesLoading } = useAPI(
        `/applications/${id}/finances`
    );

    const { data: invoice, loading: invoiceLoading } = useAPI(
        `/display_invoices/${id}/file-url`
    );

    const statusLabels = {
        open: "Open",
        pending: "Pending",
        approved: "Approved",
        denied: "Denied",
        rejected: "Rejected",
        completed: "Completed",
        prospective_scholarship: "Prospective Scholarship",
        retroactive_scholarship: "Retroactive Scholarship",
        discontinued_studies: "Discontinued Studies",
        approved_await_payment: "Approved Awaiting Payment"
    };

    return (
        <>
            <NavBar
                activeName="Business Loan"
                onChange={(label) => console.log(label)}
            />
            {(studentInfoLoading || financesLoading || invoiceLoading || businessLoading) && (
                <Box pt={"600px"}>
                    <CustomSpinner />
                </Box>
            )}
            {!studentInfoLoading && !financesLoading && !businessLoading && !invoiceLoading && studentInfo && finances && invoice && (
                <Box m={6}>
                    <Flex align="center" justify="space-between" mb={4}>
                        <Heading as="h1" size="xl">
                            {studentInfo.first_name} {studentInfo.last_name}
                        </Heading>
                        <HStack>
                            <Button
                                colorScheme="green"
                                isDisabled={businessLoans.status === "approved"}
                                onClick={async () => {
                                    try {
                                        await axios.patch(
                                            `${process.env.REACT_APP_BACKEND_URL}/business_loan/${businessLoans.id}`,
                                            { status: "approved" },
                                            { headers: { Authorization: `Bearer ${token}` } }
                                        );
                                        toast({
                                            title: "Loan Approved.",
                                            status: "success",
                                            duration: 3000,
                                            isClosable: true,
                                        });
                                        setBusinessLoans({ ...businessLoans, status: "approved" });
                                    } catch (err) {
                                        toast({
                                            title: "Error approving loan.",
                                            status: "error",
                                            duration: 3000,
                                            isClosable: true,
                                        });
                                    }
                                }}
                            >
                                Approve
                            </Button>
                            <Button
                                colorScheme="red"
                                isDisabled={businessLoans.status === "rejected"}
                                onClick={async () => {
                                    try {
                                        await axios.patch(
                                            `${process.env.REACT_APP_BACKEND_URL}/business_loan/${businessLoans.id}`,
                                            { status: "rejected" },
                                            { headers: { Authorization: `Bearer ${token}` } }
                                        );
                                        toast({
                                            title: "Loan Denied.",
                                            status: "info",
                                            duration: 3000,
                                            isClosable: true,
                                        });
                                        setBusinessLoans({ ...businessLoans, status: "rejected" });
                                    } catch (err) {
                                        toast({
                                            title: "Error denying loan.",
                                            status: "error",
                                            duration: 3000,
                                            isClosable: true,
                                        });
                                    }
                                }}
                            >
                                Deny
                            </Button>
                            <Button
                                colorScheme="blue"
                                isDisabled={businessLoans.status === "approved_await_payment"}
                                onClick={async () => {
                                    try {
                                        await axios.patch(
                                            `${process.env.REACT_APP_BACKEND_URL}/business_loan/${businessLoans.id}`,
                                            { status: "approved_await_payment" },
                                            { headers: { Authorization: `Bearer ${token}` } }
                                        );
                                        toast({
                                            title: "Loan approved, awaiting payment.",
                                            status: "info",
                                            duration: 3000,
                                            isClosable: true,
                                        });
                                        setBusinessLoans({ ...businessLoans, status: "approved_await_payment" });
                                    } catch (err) {
                                        toast({
                                            title: "Error approving await payment on loan.",
                                            status: "error",
                                            duration: 3000,
                                            isClosable: true,
                                        });
                                    }
                                }}
                            >
                                Approve, Await Payment
                            </Button>
                            <Button
                                colorScheme="yellow"
                                isDisabled={businessLoans.status === "pending"}
                                onClick={async () => {
                                    try {
                                        await axios.patch(
                                            `${process.env.REACT_APP_BACKEND_URL}/business_loan/${businessLoans.id}`,
                                            { status: "pending" },
                                            { headers: { Authorization: `Bearer ${token}` } }
                                        );
                                        toast({
                                            title: "Loan status changed to pending.",
                                            status: "info",
                                            duration: 3000,
                                            isClosable: true,
                                        });
                                        setBusinessLoans({ ...businessLoans, status: "pending" });
                                    } catch (err) {
                                        toast({
                                            title: "Error applying pending status on loan.",
                                            status: "error",
                                            duration: 3000,
                                            isClosable: true,
                                        });
                                    }
                                }}
                            >
                                Pending
                            </Button>
                        </HStack>
                    </Flex>


                    <Flex direction={{ base: "column", md: "row" }}>
                        <Box flex="0.3">
                            <Text fontSize="lg">
                                <strong>University:</strong> {studentInfo.university?.name}
                            </Text>
                            <Text fontSize="lg">
                                <strong>MAT Number:</strong> {studentInfo.mat_number}
                            </Text>
                            <Text fontSize="lg">
                                <strong>Status:</strong> {statusLabels[studentInfo.status]}
                            </Text>
                            <Text fontSize="lg">
                                <strong>Email:</strong> {studentInfo.email}
                            </Text>
                            <Text fontSize="lg">
                                <strong>Phone:</strong> {studentInfo.phone_number}
                            </Text>
                            <Text fontSize="lg">
                                <strong>Deposited - Expected:</strong> {finances.deposited_minus_expected}
                            </Text>
                            <Text fontSize="lg">
                                <strong>Percent of Total Paid Off:</strong> {(finances.total_deposited / (finances.total_deposited + finances.balance) * 100).toFixed(2) + "%"}
                            </Text>
                            <Text fontSize="lg">
                                <strong>Balance:</strong> {finances.balance}
                            </Text>
                            <Text fontSize="lg">
                                <strong>Days Since Last Deposit:</strong> {finances.days_since_last_deposit}
                            </Text>
                            <Text fontSize="lg">
                                <strong>Business Loan Score:</strong> {score ? `${score}/100` : 'Not calculated'}
                            </Text>
                            <Text fontSize="lg">
                                <strong>Business Name:</strong> {businessLoans.business_name}
                            </Text>
                            <Text fontSize="lg">
                                <strong>Business Description:</strong> {businessLoans.description}
                            </Text>
                            <Text fontSize="lg">
                                <strong>Years in Operation:</strong> {businessLoans.years}
                            </Text>
                            <Text fontSize="lg">
                                <strong>Business Industry:</strong> {businessLoans.industry}
                            </Text>
                            <Text fontSize="lg">
                                <strong>Average Monthly Sales:</strong> {businessLoans.avg_monthly_sales}
                            </Text>
                            <Text fontSize="lg">
                                <strong>Average Monthly Expenses:</strong> {businessLoans.avg_monthly_expenses}
                            </Text>
                            <Text fontSize="lg">
                                <strong>Profit Margin:</strong> {businessLoans.avg_monthly_sales - businessLoans.avg_monthly_expenses}
                            </Text>
                            <Text fontSize="lg">
                                <strong>Amount Needed:</strong> {businessLoans.loan_amount}
                            </Text>
                            <Text fontSize="lg">
                                <strong>Loan Purpose:</strong> {businessLoans.loan_purpose}
                            </Text>
                            <Text fontSize="lg">
                                <strong>Credit Score:</strong> {studentInfo.credit_score}
                            </Text>
                            <Text fontSize="lg">
                                <strong>Business Loan Status:</strong> {statusLabels[businessLoans.status]}
                            </Text>
                            <Text fontSize="lg">
                                <strong>Date Requested:</strong> {new Date(businessLoans.date).toLocaleDateString()}
                            </Text>
                            <Text fontSize="lg" mb={8}>
                                <strong>Due Date:</strong> {new Date(new Date(businessLoans.date).setMonth(new Date(businessLoans.date).getMonth() + 1)).toLocaleDateString()}
                            </Text>
                        </Box>
                        <Box flex="0.6" ml={8}>
                            <Box mx="auto" height="500px">
                                <img src={logo} alt="Invoice" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                            </Box>
                        </Box>
                    </Flex>
                    {/* <IndividualPanel id={id} /> */}
                </Box>
            )}
        </>
    );
}

export default IndividualBusinessLoan;
